import { useEffect, useState } from 'react';
import { Button, Group } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { subDays, subMonths, subYears } from 'date-fns';

interface AnalysisTimeControlsProps {
  dateRange: [Date, Date];
  onChange: (range: [Date, Date]) => void;
}

type QuickRange = { label: string; days?: number; months?: number; years?: number };

const QUICK_RANGES: QuickRange[] = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '1Y', years: 1 },
];

export function AnalysisTimeControls({ dateRange, onChange }: AnalysisTimeControlsProps): JSX.Element {
  // Local state allows intermediate [Date, null] while user picks end date
  const [pickerValue, setPickerValue] = useState<DatesRangeValue>([dateRange[0], dateRange[1]]);

  // Sync local state when parent dateRange changes (e.g. quick range buttons)
  useEffect(() => {
    setPickerValue([dateRange[0], dateRange[1]]);
  }, [dateRange]);

  const handleQuickRange = (range: QuickRange) => {
    const end = new Date();
    let start: Date;
    if (range.days) {
      start = subDays(end, range.days);
    } else if (range.months) {
      start = subMonths(end, range.months);
    } else if (range.years) {
      start = subYears(end, range.years);
    } else {
      start = subDays(end, 30);
    }
    onChange([start, end]);
  };

  const handleDateChange = (value: DatesRangeValue) => {
    if (!value || !Array.isArray(value)) return;
    setPickerValue(value);
    const [start, end] = value;
    if (start && end) {
      const startDate = start instanceof Date ? start : new Date(start);
      const endDate = end instanceof Date ? end : new Date(end);
      onChange([startDate, endDate]);
    }
  };

  return (
    <Group gap="sm" wrap="wrap">
      <Group gap={4}>
        {QUICK_RANGES.map((range) => (
          <Button key={range.label} variant="subtle" size="xs" onClick={() => handleQuickRange(range)}>
            {range.label}
          </Button>
        ))}
      </Group>
      <DatePickerInput
        type="range"
        value={pickerValue}
        onChange={handleDateChange}
        size="xs"
        w={{ base: '100%', xs: 260 }}
        allowSingleDateInRange
      />
    </Group>
  );
}
